import { DialogComponent } from '@theme/dialog';

/*
 * Average adult proportions as a fraction of standing height (ANSUR-style anthropometry):
 * the base of the neck (cervicale) sits at about 85.7% and mid-knee at about 28.5%.
 * A flat-measured back length hangs slightly shorter once it drapes over the shoulders.
 */
const CERVICALE = 0.857;
const KNEE = 0.285;
const HIPS = 0.52;
const DRAPE = 0.97;

/* Hem height as a fraction of standing height, highest first. */
const ZONES = [
  [0.47, 'hips'],
  [0.4, 'upper_thigh'],
  [0.34, 'mid_thigh'],
  [0.3, 'above_knee'],
  [0.26, 'knee'],
  [0.2, 'below_knee'],
  [0.13, 'mid_calf'],
  [-Infinity, 'ankle'],
];

/* SVG layout: the figure spans y 20 (top of head) to y 420 (floor) at any height. */
const FLOOR_Y = 420;
const FIGURE_HEIGHT = 400;
const CENTER_X = 120;
const SHOULDER_Y = 98;

const STORAGE_HEIGHT = 'ravnveil-fit-height';
const STORAGE_UNIT = 'ravnveil-fit-unit';
const SVG_NS = 'http://www.w3.org/2000/svg';

/* Left half of a front-facing figure, from the neck down the arm, torso and leg to the crotch. */
const BODY_LEFT = [
  [110, 68], [110, 84], [92, 90], [78, 96], [72, 108],
  [69, 150], [66, 200], [64, 228], [63, 250], [67, 266], [75, 268], [78, 252],
  [78, 228], [80, 180], [86, 132],
  [90, 172], [84, 212],
  [86, 250], [90, 306], [94, 360], [96, 400],
  [90, 412], [84, 420], [112, 420], [114, 404],
  [113, 360], [112, 306], [114, 262],
];

const readStorage = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Storage can be unavailable (private mode, blocked site data); the preview works without it.
  }
};

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const yFor = (fraction) => FLOOR_Y - fraction * FIGURE_HEIGHT;

/**
 * Closed Catmull-Rom spline through the points, as an SVG path.
 * @param {number[][]} points
 */
function smoothClosedPath(points) {
  const count = points.length;
  const at = (index) => points[(index + count) % count];
  let d = `M${at(0)[0]},${at(0)[1]}`;

  for (let i = 0; i < count; i++) {
    const [p0, p1, p2, p3] = [at(i - 1), at(i), at(i + 1), at(i + 2)];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0].toFixed(1)},${c1[1].toFixed(1)} ${c2[0].toFixed(1)},${c2[1].toFixed(1)} ${p2[0]},${p2[1]}`;
  }

  return `${d}Z`;
}

const BODY_PATH = smoothClosedPath([
  ...BODY_LEFT,
  [CENTER_X, 236],
  ...BODY_LEFT.slice().reverse().map(([x, y]) => [2 * CENTER_X - x, y]),
]);

/**
 * Hooded cloak outline from the hood down to the hem, flaring slightly with length.
 * The face opening is cut out with the even-odd rule.
 * @param {number} hemY
 */
function cloakPath(hemY) {
  const half = 50 + Math.max(0, hemY - SHOULDER_Y) * 0.075;
  const left = CENTER_X - half;
  const right = CENTER_X + half;
  const dip = 6;

  const outline = [
    `M${CENTER_X},12`,
    `C140,12 150,30 149,56`,
    `C148,74 150,86 162,94`,
    `C172,100 174,106 175,112`,
    `L${right},${hemY}`,
    `Q${CENTER_X},${hemY + dip * 2} ${left},${hemY}`,
    `L65,112`,
    `C66,106 68,100 78,94`,
    `C90,86 92,74 91,56`,
    `C90,30 100,12 ${CENTER_X},12`,
    'Z',
  ].join(' ');

  const face = 'M120,24 C131,24 135,36 135,48 C135,62 128,72 120,72 C112,72 105,62 105,48 C105,36 109,24 120,24 Z';

  return `${outline} ${face}`;
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLScriptElement} strings
 * @property {HTMLInputElement} height
 * @property {HTMLOutputElement} heightOutput
 * @property {HTMLButtonElement[]} unitButtons
 * @property {HTMLElement} sizes
 * @property {HTMLElement} result
 * @property {HTMLElement} detail
 * @property {HTMLElement} status
 * @property {SVGSVGElement} figure
 *
 * @extends DialogComponent<Refs>
 */
class FitPreview extends DialogComponent {
  requiredRefs = ['dialog', 'strings', 'height', 'heightOutput', 'sizes', 'result', 'detail', 'status', 'figure'];

  /** @type {{ size: string, cm: number }[]} */
  #sizes = [];
  #size = '';
  #unit = 'in';
  #hemY = yFor(0.4);
  #frame = 0;
  /** @type {Record<string, any>} */
  #strings = {};
  /** @type {Record<string, SVGElement>} */
  #shapes = {};

  connectedCallback() {
    super.connectedCallback();

    this.#sizes = this.#readBackLengths();
    if (this.#sizes.length === 0) {
      this.hidden = true;
      return;
    }

    try {
      this.#strings = JSON.parse(this.refs.strings.textContent || '{}');
    } catch (error) {
      this.hidden = true;
      return;
    }

    const storedHeight = Number(readStorage(STORAGE_HEIGHT));
    if (storedHeight >= Number(this.refs.height.min) && storedHeight <= Number(this.refs.height.max)) {
      this.refs.height.value = String(storedHeight);
    }
    this.#unit = readStorage(STORAGE_UNIT) === 'cm' ? 'cm' : 'in';

    // Start from L (a common fit) when the shopper hasn't picked a size yet.
    const fallback = this.#sizes.find((entry) => entry.size === 'L') || this.#sizes[Math.floor(this.#sizes.length / 2)];
    this.#size = this.#selectedProductSize() || fallback.size;

    this.#buildSizeOptions();
    this.#buildFigure();
    this.#syncUnitButtons();
    this.#render({ animate: false, announce: false });
    this.hidden = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.#frame);
  }

  showDialog() {
    const selected = this.#selectedProductSize();
    if (selected && selected !== this.#size) {
      this.#size = selected;
      this.#checkSize();
      this.#render({ animate: false, announce: false });
    }
    super.showDialog();
  }

  /** Live update while dragging the height slider. */
  onHeightInput() {
    this.#render({ animate: false, announce: false });
  }

  /** Announce and remember the height once the slider is released. */
  onHeightCommit() {
    writeStorage(STORAGE_HEIGHT, this.refs.height.value);
    this.#render({ animate: false, announce: true });
  }

  /** Size radios report through their fieldset, so read the checked one directly. */
  onSizeChange() {
    const input = this.refs.sizes.querySelector('input:checked');
    if (!(input instanceof HTMLInputElement) || input.value === this.#size) return;
    this.#size = input.value;
    this.#render({ animate: true, announce: true });
  }

  /** @param {Event} event */
  setUnit(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-unit]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#unit = button.dataset.unit === 'cm' ? 'cm' : 'in';
    writeStorage(STORAGE_UNIT, this.#unit);
    this.#syncUnitButtons();
    this.#render({ animate: false, announce: true });
  }

  /** Reads sizes and centimeter back lengths from the size guide table on the same page. */
  #readBackLengths() {
    const scope = this.closest('.shopify-section') || document;
    const table =
      scope.querySelector('.size-guide__content [data-unit-table="cm"]') ||
      document.querySelector('.size-guide__content [data-unit-table="cm"]');
    if (!(table instanceof HTMLTableElement)) return [];

    const headers = Array.from(table.querySelectorAll('thead th')).slice(1).map((cell) => cell.textContent.trim());
    const row = Array.from(table.querySelectorAll('tbody tr')).find((tr) => {
      const label = tr.querySelector('th');
      return label && /^back length$/i.test(label.textContent.trim());
    });
    if (!row) return [];

    const values = Array.from(row.querySelectorAll('td')).map((cell) => parseFloat(cell.textContent.replace(',', '.')));

    return headers
      .map((size, index) => ({ size, cm: values[index] }))
      .filter((entry) => entry.size && Number.isFinite(entry.cm) && entry.cm > 0);
  }

  /**
   * The size the shopper chose in the product form, when it matches a size in the guide.
   * Product pages preselect the first size, so only follow it once a variant was picked (?variant=).
   */
  #selectedProductSize() {
    if (!new URL(window.location.href).searchParams.has('variant')) return '';
    const scope = this.closest('.shopify-section') || document;
    const known = new Set(this.#sizes.map((entry) => entry.size));
    for (const input of scope.querySelectorAll('input[type="radio"]:checked')) {
      if (input instanceof HTMLInputElement && known.has(input.value)) return input.value;
    }
    return '';
  }

  #buildSizeOptions() {
    const { sizes, dialog } = this.refs;
    const name = `${dialog.getAttribute('aria-labelledby') || 'fit-preview'}-size`;
    sizes.replaceChildren();

    for (const entry of this.#sizes) {
      const label = document.createElement('label');
      label.className = 'fit-preview__size';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = entry.size;
      input.checked = entry.size === this.#size;

      const text = document.createElement('span');
      text.textContent = entry.size;

      label.append(input, text);
      sizes.append(label);
    }
  }

  #checkSize() {
    for (const input of this.refs.sizes.querySelectorAll('input')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === this.#size;
    }
  }

  #syncUnitButtons() {
    for (const button of this.refs.unitButtons || []) {
      button.setAttribute('aria-pressed', String(button.dataset.unit === this.#unit));
    }
  }

  /**
   * @param {string} tag
   * @param {Record<string, string | number>} attributes
   */
  #svg(tag, attributes) {
    const element = document.createElementNS(SVG_NS, tag);
    for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value));
    return element;
  }

  #buildFigure() {
    const { figure } = this.refs;
    figure.replaceChildren();

    const guide = (fraction, label) => {
      const y = yFor(fraction);
      figure.append(
        this.#svg('line', { class: 'fit-preview__guide', x1: 18, x2: 222, y1: y, y2: y }),
        Object.assign(this.#svg('text', { class: 'fit-preview__svg-label', x: 0, y: y - 4 }), { textContent: label })
      );
    };

    figure.append(this.#svg('line', { class: 'fit-preview__floor', x1: 10, x2: 230, y1: FLOOR_Y, y2: FLOOR_Y }));
    guide(HIPS, this.#strings.hips || 'Hips');
    guide(KNEE, this.#strings.knee || 'Knee');

    figure.append(
      this.#svg('path', { class: 'fit-preview__body-shape', d: BODY_PATH }),
      this.#svg('ellipse', { class: 'fit-preview__body-shape', cx: CENTER_X, cy: 45, rx: 18, ry: 24 })
    );

    this.#shapes.cloak = this.#svg('path', { class: 'fit-preview__cloak', 'fill-rule': 'evenodd' });
    this.#shapes.seam = this.#svg('line', { class: 'fit-preview__guide', x1: CENTER_X, x2: CENTER_X, y1: 74 });
    this.#shapes.hem = this.#svg('line', { class: 'fit-preview__hem', x1: 24, x2: 216 });
    this.#shapes.hemLabel = this.#svg('text', {
      class: 'fit-preview__svg-label fit-preview__svg-label--hem',
      x: 240,
      'text-anchor': 'end',
    });
    this.#shapes.hemLabel.textContent = this.#strings.hem || 'Hem';

    figure.append(this.#shapes.cloak, this.#shapes.seam, this.#shapes.hem, this.#shapes.hemLabel);
  }

  /** @param {number} hemY */
  #drawHem(hemY) {
    this.#hemY = hemY;
    this.#shapes.cloak.setAttribute('d', cloakPath(hemY));
    this.#shapes.seam.setAttribute('y2', String(hemY + 6));
    this.#shapes.hem.setAttribute('y1', String(hemY));
    this.#shapes.hem.setAttribute('y2', String(hemY));
    this.#shapes.hemLabel.setAttribute('y', String(hemY - 5));
  }

  /** @param {number} target */
  #animateHem(target) {
    cancelAnimationFrame(this.#frame);
    const start = this.#hemY;
    if (reducedMotion() || Math.abs(target - start) < 0.5) {
      this.#drawHem(target);
      return;
    }

    const began = performance.now();
    const duration = 320;
    const step = (now) => {
      const progress = Math.min(1, (now - began) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.#drawHem(start + (target - start) * eased);
      if (progress < 1) this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }

  /** @param {number} cm */
  #formatLength(cm) {
    if (this.#unit === 'cm') return `${Math.round(cm)} cm`;
    return `${Math.round(cm / 2.54)} in`;
  }

  /** @param {number} cm */
  #formatHeight(cm) {
    const totalInches = Math.round(cm / 2.54);
    const imperial = `${Math.floor(totalInches / 12)}′${totalInches % 12}″`;
    const metric = `${Math.round(cm)} cm`;
    return this.#unit === 'cm' ? `${metric} (${imperial})` : `${imperial} (${metric})`;
  }

  /** @param {{ animate: boolean, announce: boolean }} options */
  #render({ animate, announce }) {
    const entry = this.#sizes.find((item) => item.size === this.#size);
    if (!entry) return;

    const height = Number(this.refs.height.value);
    const heightText = this.#formatHeight(height);
    this.refs.heightOutput.textContent = heightText;
    this.refs.height.setAttribute('aria-valuetext', heightText);

    const hemCm = CERVICALE * height - entry.cm * DRAPE;
    const fraction = hemCm / height;
    const zoneKey = (ZONES.find(([threshold]) => fraction >= threshold) || ZONES[ZONES.length - 1])[1];
    const zone = (this.#strings.zones && this.#strings.zones[zoneKey]) || zoneKey;

    const fromKnee = hemCm - KNEE * height;
    let detail;
    if (Math.abs(fromKnee) < 2.5) {
      detail = this.#strings.at || '';
    } else {
      const template = fromKnee > 0 ? this.#strings.above : this.#strings.below;
      detail = (template || '').replace('%distance%', this.#formatLength(Math.abs(fromKnee)));
    }

    const result = (this.#strings.result || '').replace('%size%', entry.size).replace('%zone%', zone);
    this.refs.result.textContent = result;
    this.refs.detail.textContent = detail;
    this.refs.figure.setAttribute(
      'aria-label',
      (this.#strings.figure || '').replace('%size%', entry.size).replace('%zone%', zone)
    );

    const hemY = Math.min(FLOOR_Y - 2, Math.max(SHOULDER_Y + 40, yFor(fraction)));
    if (animate) {
      this.#animateHem(hemY);
    } else {
      cancelAnimationFrame(this.#frame);
      this.#drawHem(hemY);
    }

    if (announce) this.refs.status.textContent = `${result} ${detail}`;
  }
}

if (!customElements.get('fit-preview')) customElements.define('fit-preview', FitPreview);
